import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from "@mui/material/Box"

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isUndefined, isEqual, cloneDeep } from "lodash";

import AutocompleteSearch from "@plannel/components/AutocompleteSearch";
import routeService from "@plannel/services/data/route-service"
import resourceService from "@plannel/services/data/resource-service"
import simResourceService from "@plannel/services/data/sim-resource-service"
import workcenterService from "@plannel/services/data/workcenter-service"
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter"
import PaginationContainer from "@plannel/components/PaginationContainer";
import Dialog from "@plannel/components/Dialog";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting, { nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import NumberUtil from "@plannel/utils/number-util";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = ColorUtil;

const SimResourceMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const [locationFilter, setLocationFilter] = useState(reduxViewState?.locationFilter ?? { locations: null });
  const [workcenterSearch, setWorkcenterSearch] = useState(reduxViewState?.workcenterSearch ?? null);
  const [workcenterOptions, setWorkcenterOptions] = useState([]);
  const [routeSearch, setRouteSearch] = useState(reduxViewState?.routeSearch ?? null);
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  // const [progressOn, setProgressOn] = useState(false);

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [simResource, setSimResource] = useState([]);
  const [route, setRoute] = useState([]);
  const [resource, setResource] = useState([]);
  const resourceMap= useRef({});
  const routeMap= useRef({});

  const [resourceSearch, setResourceSearch] = useState(reduxViewState?.resourceSearch ?? null);
  const EXCEL_EXPORT = "excelExport";

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dropdownValueFormatter = (data) => {
    if (typeof data?.value === "string") {
      return data.value;
    }

    return data.value ? data.value.routeCd : "";
  }

  const dropdownResourceValueFormatter = (data) => {
    if (typeof data?.value === "string") {
      return data.value;
    }

    return data.value ? data.value.resCd : "";
  }

  const percentFormatter = (params, isExport = false) => {
    const value = params?.data?.[params?.colDef?.field];
    if (isExport) {
      return NumberUtil.formatNumber(value, { maximumFractionDigits: 4, minimumFractionDigits: 4 });
    }
    return `${value}%`;
  }
  percentFormatter.customType = EXCEL_EXPORT;

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    
    return nullSafeComparator(val1, val2, isDescending);
  }

  const onCellValueChanged = (params) => {
    const { column, node, value } = params;
    const colId = column.getColId();
    if (isEqual(colId, "mainResCd")) {
      node.data.mainResId = resourceMap.current[params.value].id;
      node.data.wcCd = resourceMap.current[params.value].workcenterCd;
      node.data.locCd = resourceMap.current[params.value].locCd;
      node.data.subResCd = null;
      node.data.subResId = null;
      params.api.refreshCells({ rowNodes: [node], columns: ["subResCd"], force: true });
    } else if (isEqual(colId, "routeCd")) {
      node.data.itemCd = routeMap.current[params.value].item?.itemCd;
      node.data.routeName = routeMap.current[params.value].name;
    } else if (isEqual(colId, "timeBase")) {
      if (node.data.lotSize < 1) {
        node.setDataValue("lotSize", 1);
      }
    } else if (isEqual(colId, "lotSize")) {
      if (value < 0) {
        params.newValue = 0;
      }

      if (node.data.timeBase === "lot") {
        if (!value || value === 0) {
          params.newValue = 1;
        }
      }

      node.setDataValue("lotSize", params.newValue);
    }

    defaultGridMemo.onCellValueChanged(params);
  }

  const columnDefs = [
    {
      headerName: "locCd",
      field: "locCd",
      minWidth: 150,
      type: ["nonEditableColumn"],
      flex: 2,
      cellClass: 'stringType',
      filterType: 'string'
    },
    {
      headerName: "wcCd",
      field: "wcCd",
      minWidth: 150,
      type: ["nonEditableColumn"],
      flex: 2,
      cellClass: 'stringType',
      filterType: 'string'
    },
    {
      headerName: "itemCd",
      field: "itemCd",
      minWidth: 150,
      type: ["nonEditableColumn"],
      flex: 2,
      cellClass: 'stringType',
      filterType: 'string'
    },
    {
      headerName: "routeCd", field: "routeCd",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownValueFormatter,
      cellEditorParams: {
        values: reduxViewState?.route ?? route
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 150,
      filterType: 'string',
    },
    {
      headerName: "routeName",
      field: "routeName",
      minWidth: 150,
      type: ["nonEditableColumn"],
      flex: 2,
      cellClass: 'stringType',
      filterType: 'string'
    },
    {
      headerName: "mainResCd", field: "mainResCd",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownResourceValueFormatter,
      cellEditorParams: {
        valueFormatter: dropdownResourceValueFormatter,
        values: reduxViewState?.resource ?? resource
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 150,
      filterType: 'string',
    },
    {
      headerName: "subResCd", field: "subResCd",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      editable: (params) => !!params.data.mainResCd,
      cellStyle: (params) => !params.data.mainResCd ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" } : null,
      valueFormatter: dropdownResourceValueFormatter,
      cellEditorParams: (params) => {
        const mainResCd = params.data.mainResCd;
        if (!mainResCd) return { valueFormatter: dropdownResourceValueFormatter, values: [] };
        const mainRes = resourceMap.current[mainResCd];
        const filtered = mainRes
          ? resource.filter(resCd => {
              const res = resourceMap.current[resCd];
              return res && res.locCd === mainRes.locCd && res.workcenterCd === mainRes.workcenterCd && resCd !== mainResCd;
            })
          : [];
        return { valueFormatter: dropdownResourceValueFormatter, values: filtered };
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 150,
      filterType: 'string',
    },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 100, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: "string" },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: "string" }
  ];

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (locationFilter?.locations?.length > 0) {
      searchFilters.locationCd = locationFilter.locations.map(l => l.locCd);
    }

    if (workcenterSearch) {
      searchFilters.workCenterCd = workcenterSearch.wcCd;
    }

    if (routeSearch) {
      searchFilters.routeCd = routeSearch;
    }

    if (resourceSearch) {
      searchFilters.resCd = resourceSearch.resCd;
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveSimResource = () => {
    const params = getRequestParams();
    simResourceService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setSimResource(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { locationFilter, workcenterSearch, routeSearch, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      })
  }

  const retrieveWorkcenter = () => {
    workcenterService.getAll({ pagination: false })
      .then(response => {
        const { results } = response.data;
        console.log("workcenter results", results);
        setWorkcenterOptions(results ?? []);
      })
      .catch(e => { debug(e); });
  }

  const retrieveRoute = () => {
    routeService.getAll()
      .then(response => {
        const route = [];
        const { results } = response.data;
        for (const res of results) {
          route.push(res.routeCd);
          routeMap.current[res.routeCd] = res;
        }
        setRoute(route);
      })
      .catch(e => {
        debug(e);
      });
  }

  const retrieveResource = () => {
    resourceService.getAll()
      .then(response => {
        const { results } = response.data;
        const resource = [];
        for (const res of results) {
          resource.push(res.resCd);
          resourceMap.current[res.resCd] = res;
        }
        setResource(resource);
      })
      .catch(e => {
        debug(e);
      });
  }

  const onGridReady = params => {
    retrieveRoute();
    retrieveResource();
    retrieveWorkcenter();
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const insertRow = () => {
    const newRow = {
      id: null,
      locCd: null,
      wcCd: null,
      routeCd: null,
      routeName: null,
      mainResId: null,
      mainResCd: null,
      subResId: null,
      subResCd: null,
      itemCd: null,
      activeFlg: true,
      verNum: null
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  const removeSimResource = () => {
    const simResourceIds = [];

    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        simResourceIds.push(data.id)
      }
    );
    if (simResourceIds.length > 0) {
      simResourceService.remove(simResourceIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveSimResource();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const saveSimResource = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    for (const row of allStateData) {
      if (row.routeCd === null) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("route")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      if (row.mainResCd === null) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("mainRes")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      if (row.subResCd === null) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("subRes")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      if (row.mainResCd) {
        row.mainResId = resourceMap.current[row.mainResCd].id;
        // row.resourceDto = resourceMap.current[row.mainResCd];
      }

      if (row.subResCd) {
        row.subResId = resourceMap.current[row.subResCd].id;
      }

      if (row.routeCd) {
        row.routeId = routeMap.current[row.routeCd].id;
      }
    }
    gridRef.current.api.stopEditing();
    if (allStateData.length > 0) {
      simResourceService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          // setProgressOn(false);
          retrieveSimResource();
        });
    } else {
      // setProgressOn(false)
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      // setProgressOn(true)
      saveSimResource();
    } else if (action === "DELETE") {
      removeSimResource();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveSimResource();
    }
  }

  const getCsvColDefs = () => {
    const csvColDefs = cloneDeep(columnDefs)
    for (const col of csvColDefs) {
      if (col.valueFormatter?.customType === EXCEL_EXPORT) {
        col.cellRenderer = null;
        col.valueFormatter = null;
        col.valueGetter = params => percentFormatter(params, true);
        col.cellClass = params => {
          const value = Number(params.value);
          return !isNaN(value) && !Number.isInteger(value) ? 'flexibleDecimalType' : '';
        }
      }
    }
    return csvColDefs
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (!isUndefined(resource) && !isEmpty(resource)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource]);

  useEffect(() => {

    if (!isUndefined(route) && !isEmpty(route)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveSimResource();
    }

    return () => {
      setSimResource([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }
    return () => {
      setSimResource([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, locationFilter, workcenterSearch, routeSearch, resourceSearch]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <MPLocationTreeFilter
          viewName={viewName}
          filterValue={locationFilter?.locations?.map(l => l.locCd)}
          onChangeFilter={setLocationFilter}
        />
        <Autocomplete
          options={workcenterOptions}
          getOptionLabel={option => option.wcCd ?? ""}
          value={workcenterSearch}
          onChange={(_, newValue) => setWorkcenterSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.wcCd === value.wcCd}
          size="small"
          sx={{ pr: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField {...params} label={t("workcenter")} />
          )}
        />
        <Autocomplete
          options={route}
          getOptionLabel={option => option}
          value={routeSearch}
          onChange={(_, newValue) => setRouteSearch(newValue)}
          isOptionEqualToValue={(option, value) => option === value}
          size="small"
          sx={{ pr: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField {...params} label={t("route")} />
          )}
        />
        <AutocompleteSearch label="mainRes" value={resourceSearch} setValue={setResourceSearch} optionLabelProp="name" optionEqualProp="resCd"
          target="resource" />
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto", mr: 1 }}>
          <ExcelExportButton title={title} service={simResourceService} params={getRequestParams()} columnDefs={getCsvColDefs()} />
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="sim-resource-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={simResource}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onCellValueChanged={onCellValueChanged}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      {/* <Backdrop sx={{ color: '#fff', zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop> */}
    </>
  );
};

export default withTranslation()(SimResourceMaster);
